import React, { useState, useEffect, createContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import api from './utils/api';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ConsultationPage from './pages/ConsultationPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetail from './pages/ClientDetail';
import TasksPage from './pages/TasksPage';
import PaymentsPage from './pages/PaymentsPage';
import CreditorDbPage from './pages/CreditorDbPage';
import RoadmapPage from './pages/RoadmapPage';
import RemoteClientsPage from './pages/RemoteClientsPage';
import PersonalTasksPage from './pages/PersonalTasksPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ReferralPage from './pages/ReferralPage';
import UsefulPage from './pages/UsefulPage';
import JudgesPage from './pages/JudgesPage';
import MyDocsPage from './pages/MyDocsPage';
import ContactsPage from './pages/ContactsPage';
import ShopOrdersPage from './pages/ShopOrdersPage';
import TradesPage from './pages/TradesPage';
import TradeDetail from './pages/TradeDetail';
import LegalDocsPage from './pages/LegalDocsPage';
import HabitsPage from './pages/HabitsPage';
import FinancePage from './pages/FinancePage';
import MfcClientsPage from "./pages/MfcClientsPage";
import MfcClientProfilePage from "./pages/MfcClientProfilePage";
import MfcChatPage from "./pages/MfcChatPage";
import MfcContractPage from "./pages/MfcContractPage";
import MfcAnalyticsPage from "./pages/MfcAnalyticsPage";
import CasesPage from './pages/CasesPage';
import CaseDetail from './pages/CaseDetail';
import HearingsPage from './pages/HearingsPage';
import CasesAnalyticsPage from './pages/CasesAnalyticsPage';
import InquiriesPage from './pages/InquiriesPage';
import MarketingPage from './pages/MarketingPage';
import BackupPage from './pages/BackupPage';

export const AuthContext = createContext(null);
export const ToastContext = createContext(null);
export const BadgesContext = createContext({ tasks: 0, payments: 0 });

function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type='success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toast && <div className={`toast toast-${toast.type}`}>{toast.message}</div>}
    </ToastContext.Provider>
  );
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({ tasks: 0, payments: 0 });
  const location = useLocation();

  const fetchBadges = useCallback(() => {
    api.get('/badges').then(r => setBadges(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  useEffect(() => { setSidebarOpen(false); }, [location]);

  return (
    <BadgesContext.Provider value={badges}>
      <div className="app-layout">
        <button className="mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>{'\u2630'}</button>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="main-content">
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/clients" element={<ClientsPage clientType="mfc" />} />
            <Route path="/clients-court" element={<ClientsPage clientType="court" />} />
            <Route path="/clients-unknown" element={<ClientsPage clientType="unknown" />} />
            <Route path="/clients-noshow" element={<ClientsPage clientType="noshow" />} />
            <Route path="/clients-thinking" element={<ClientsPage clientType="thinking" />} />
            <Route path="/clients-analysis" element={<ClientsPage clientType="analysis" />} />
            <Route path="/clients-paused" element={<ClientsPage clientType="paused" />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/cases/:id" element={<CaseDetail />} />
            <Route path="/hearings" element={<HearingsPage />} />
            <Route path="/cases-analytics" element={<CasesAnalyticsPage />} />
            <Route path="/inquiries" element={<InquiriesPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/creditor-db" element={<CreditorDbPage />} />
            <Route path="/roadmap" element={<RoadmapPage />} />
            <Route path="/remote-clients" element={<RemoteClientsPage />} />
            <Route path="/personal-tasks" element={<PersonalTasksPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/referrals" element={<ReferralPage />} />
            <Route path="/judges" element={<JudgesPage />} />
            <Route path="/my-docs" element={<MyDocsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/shop-orders" element={<ShopOrdersPage />} />
            <Route path="/trades" element={<TradesPage />} />
            <Route path="/trades/:id" element={<TradeDetail />} />
            <Route path="/legal-docs" element={<LegalDocsPage />} />
            <Route path="/useful" element={<UsefulPage />} />
            <Route path="/backup" element={<BackupPage />} />
            <Route path="/habits" element={<HabitsPage />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/mfc-service" element={<MfcClientsPage />} />
            <Route path="/mfc-service/:id" element={<MfcClientProfilePage />} />
            <Route path="/mfc-service/:id/chat" element={<MfcChatPage />} />
            <Route path="/mfc-service/:id/contract" element={<MfcContractPage />} />
            <Route path="/mfc-analytics" element={<MfcAnalyticsPage />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="*" element={<Navigate to="/tasks" replace />} />
          </Routes>
        </div>
      </div>
    </BadgesContext.Provider>
  );
}

function PrivateRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/auth/me')
      .then(r => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',color:'#7c6cf0',fontSize:18}}>Загрузка...</div>;
  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/tasks" replace /> : <LoginPage />} />
            <Route path="/*" element={
              <PrivateRoute user={user}><AppLayout /></PrivateRoute>
            } />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthContext.Provider>
  );
}
